<script setup>
// One feature, one row, on /features. Columns: icon, name, what you get, tag,
// chevron. Under 760px the blurb drops beneath the name. A row is a real link
// when the feature has a page of its own (the formats) and a real button
// otherwise, so keyboards and crawlers both get the right thing.
import {FEATURE_TAGS} from '~/utils/features';

defineProps({
  item: {type: Object, required: true},
});
const emit = defineEmits(['open']);
</script>

<template>
  <NuxtLink v-if="item.to" :to="item.to" class="fr">
    <span class="fr-icon"><v-icon size="18" :icon="item.icon"/></span>
    <span class="fr-main">
      <h3 class="fr-title">
        {{ item.title }}
        <span v-if="item.tag" class="fr-tag" :class="`fr-tag--${item.tag}`">{{ FEATURE_TAGS[item.tag] }}</span>
      </h3>
      <span class="fr-blurb">{{ item.blurb }}</span>
    </span>
    <span class="fr-go"><span class="fr-go-text">See how it works</span><v-icon size="16" icon="mdi-arrow-right"/></span>
  </NuxtLink>
  <button v-else type="button" class="fr" @click="emit('open', item)">
    <span class="fr-icon"><v-icon size="18" :icon="item.icon"/></span>
    <span class="fr-main">
      <h3 class="fr-title">
        {{ item.title }}
        <span v-if="item.tag" class="fr-tag" :class="`fr-tag--${item.tag}`">{{ FEATURE_TAGS[item.tag] }}</span>
      </h3>
      <span class="fr-blurb">{{ item.blurb }}</span>
    </span>
    <span class="fr-go"><v-icon size="18" icon="mdi-chevron-right"/></span>
  </button>
</template>

<style scoped lang="scss">
.fr {
  display: grid;
  grid-template-columns: 32px minmax(0, 1fr) auto;
  gap: 12px;
  align-items: center;
  width: 100%;
  padding: 12px 14px;
  text-align: left;
  text-decoration: none;
  color: inherit;
  font: inherit;
  background: transparent;
  border: 0;
  border-bottom: 1px solid rgb(var(--v-theme-borderLight));
  cursor: pointer;
  transition: background 0.12s ease;

  &:last-child {
    border-bottom: 0;
  }

  &:hover,
  &:focus-visible {
    background: rgb(var(--v-theme-light));
    outline: none;
  }

  &:focus-visible {
    box-shadow: inset 0 0 0 2px rgba(211, 41, 70, 0.35);
  }

  /* The last column is a fixed width, so a row with "See how it works" has
     the same middle cell as a row with only a chevron and the name and blurb
     columns line up down the whole table. */
  @media (min-width: 760px) {
    grid-template-columns: 32px minmax(0, 1fr) 136px;
    padding: 11px 16px;
  }
}

.fr-icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border-radius: 10px;
  color: rgb(var(--v-theme-primary));
  background: rgb(var(--v-theme-brandTint));
}

.fr-main {
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 0;

  @media (min-width: 760px) {
    display: grid;
    grid-template-columns: minmax(200px, 34%) minmax(0, 1fr);
    gap: 16px;
    align-items: center;
  }
}

.fr-title {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 6px;
  font-size: 14px;
  font-weight: 700;
  letter-spacing: -0.2px;
  line-height: 1.3;
  margin: 0;
}

.fr-blurb {
  font-size: 13px;
  line-height: 1.5;
  color: rgb(var(--v-theme-charcoal));
}

.fr-tag {
  font-size: 9.5px;
  font-weight: 800;
  letter-spacing: 0.7px;
  text-transform: uppercase;
  border-radius: 999px;
  padding: 2px 7px;
  color: rgb(var(--v-theme-primary));
  background: rgb(var(--v-theme-brandTint));

  &--soon {
    color: rgb(var(--v-theme-charcoal));
    background: rgb(var(--v-theme-light));
    border: 1px solid rgb(var(--v-theme-border));
  }

  &--app {
    color: rgb(var(--v-theme-info));
    background: #E8F1FB;
  }
}

.fr-go {
  display: inline-flex;
  align-items: center;
  justify-content: flex-end;
  gap: 4px;
  color: rgb(var(--v-theme-primary));
  font-size: 12.5px;
  font-weight: 700;
  white-space: nowrap;
}

.fr-go-text {
  display: none;

  @media (min-width: 760px) {
    display: inline;
  }
}
</style>
